import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import RoleIcon from 'components/RoleIcon/RoleIcon';
import TextField from 'components/TextField/TextField';
import AppRoutes from 'constants/AppRoutes';
import {
  ORDER_KEY, ORDER_TYPE, SEARCH, SORT,
  TASK_ASSIGNED_TO,
} from 'constants/formFields';
import { TASK_TYPE_ACTION } from 'constants/general';
import { BUTTON_VARIANT, ORDERS } from 'constants/interfaces';
import useDebouncedCallback from 'hooks/useDebouncedCallback';
import useUserManagementForm from 'pages/CompanyAdminPages/UserManagement/hooks/useUserManagementForm';
import { useAppDispatch } from 'store/hooks/useApp';
import { useCompanyAdminUsersListQuery } from 'store/slices/companyAdminUsers/apis/companyAdminUsersApi';
import { resetState } from 'store/slices/companyAdminUsers/slice';

import type { ISearchInputHandler } from 'components/TextField/interfaces/ITextField';
import type ISearchParams from 'pages/CompanyAdminPages/UserManagement/interfaces/ISearchParams';
import type IUserResponse from 'store/slices/user/interfaces/IUserResponse';
import type { ITaskAssignedTo } from './interfaces/IAddUserModal';
import type IAddUserModal from './interfaces/IAddUserModal';

import styles from './scss/AddUserModal.module.scss';

function AddUserModal({
  open, onClose, setValueForm, currentSelectedUsers, typeModal,
}: IAddUserModal) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [params, setParams] = useState<ISearchParams>({});
  const [orderBy, setOrderBy] = useState('');
  const [tempSelectedUsers, setTempSelectedUsers] = useState<ITaskAssignedTo[]>(currentSelectedUsers || []);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
  } = useUserManagementForm();

  const { data, isFetching } = useCompanyAdminUsersListQuery({
    search: params.search,
    filterByStatus: params.filterByStatus?.value || '',
    orderBy,
  });

  const onCreateNewUser = (() => {
    setIsConfirmationModalOpen(true);
  });

  const onDiscardChangesConfirm = () => {
    dispatch(resetState());
    navigate(AppRoutes.createNewUser, { state: { redirectPath: AppRoutes.userManagement } });
  };

  const debouncedSearch = useDebouncedCallback(() => {
    const { search } = getValues();
    setParams((prev) => ({ ...prev, search }));
  });

  const onSearchChange = ({ search, onChange }: ISearchInputHandler) => {
    onChange(search);
    debouncedSearch();
  };

  const onUserManagementSubmit = handleSubmit(() => {});

  useEffect(() => {
    if (currentSelectedUsers && typeModal === TASK_TYPE_ACTION.EDIT) {
      setValueForm(TASK_ASSIGNED_TO, currentSelectedUsers);
    }
    setTempSelectedUsers(currentSelectedUsers || []);
  }, [currentSelectedUsers, setValueForm, typeModal]);

  const handleUserSelect = (user: IUserResponse) => {
    const exists = tempSelectedUsers.some((u) => u.value === String(user.id));
    let updated: ITaskAssignedTo[];
    if (exists) {
      updated = tempSelectedUsers.filter((u) => u.value !== String(user.id));
    } else {
      updated = [...tempSelectedUsers, {
        value: String(user.id),
        label: `${user.firstName} ${user.lastName}`,
        position: '',
        role: user.role,
        email: user.email,
      }];
    }
    setTempSelectedUsers(updated);
  };

  const isUserSelected = (id: number) => tempSelectedUsers.some((u) => u.value === String(id));

  const onCancelUserSelect = () => {
    onClose();
    setTempSelectedUsers(currentSelectedUsers || []);
  };

  const unselectAll = () => {
    setTempSelectedUsers([]);
  };

  return (
    <Modal
      open={open}
      isButtonsVisible={false}
      className={styles.modal}
      title={t('taskManagement.selectEmployee')}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.search}>
            <Controller
              control={control}
              name={SEARCH}
              render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <TextField
                  name={name}
                  value={value}
                  placeholder={t('userManagement.searchPlaceholder')}
                  error={error?.message}
                  icon={<SearchIcon />}
                  onChange={(event) => {
                    onChange(event.target.value);
                    onSearchChange({ search: event.target.value, onChange });
                    onUserManagementSubmit();
                  }}
                  required
                />
              )}
            />
          </div>
          <Button
            title={t('common.addNewUser')}
            onClick={onCreateNewUser}
            className={styles.button}
          />
        </div>
        <div className={styles.usersTable}>
          {isFetching ? <Loader isPageLoading={isFetching} /> : null}
          {!isFetching && data?.data?.items && data.data.items.length > 0 ? (
            <>
              <div className={styles.selectedCounter}>
                {`Selected ${tempSelectedUsers.length}/${data.data.items.length}`}
              </div>
              <table>
                <thead>
                  <tr className={styles.tableHeader}>
                    <th className={styles.checkboxColumn}>
                      <Checkbox
                        name="user-all"
                        className={clsx(styles.checkbox, [tempSelectedUsers.length === 0 && styles.disabled])}
                        checked={tempSelectedUsers.length > 0}
                        value={String(tempSelectedUsers.length > 0)}
                        onChange={unselectAll}
                        label={false}
                        aria-label={t('common.select')}
                        disabled={tempSelectedUsers.length === 0}
                        isLineMark
                      />
                    </th>
                    <th className={styles.nameColumn}>
                      <div className={styles.sortableHeader}>
                        <span>{t('taskManagement.name')}</span>
                        <div className={styles.sortArrows}>
                          <button
                            type="button"
                            className={`${styles.sortButton} ${orderBy === 'firstName' ? styles.active : ''}`}
                            onClick={() => {
                              setValue(SORT, { [ORDER_KEY]: 'firstName', [ORDER_TYPE]: ORDERS.asc });
                              setOrderBy('firstName');
                            }}
                            tabIndex={0}
                            aria-label={t('taskManagement.sortNameAsc')}
                          >
                            <ArrowRightIcon className={styles.arrowUp} />
                          </button>
                          <button
                            type="button"
                            className={`${styles.sortButton} ${orderBy === '-firstName' ? styles.active : ''}`}
                            onClick={() => {
                              setValue(SORT, { [ORDER_KEY]: 'firstName', [ORDER_TYPE]: ORDERS.desc });
                              setOrderBy('-firstName');
                            }}
                            tabIndex={0}
                            aria-label={t('taskManagement.sortNameDesc')}
                          >
                            <ArrowRightIcon className={styles.arrowDown} />
                          </button>
                        </div>
                      </div>
                    </th>
                    <th className={styles.roleColumn}>
                      {t('userManagement.role')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.items.map((user: IUserResponse) => (
                    <tr key={user.id} className={styles.tableRow}>
                      <td className={styles.tableCellCheckbox}>
                        <Checkbox
                          name={`user-${user.id}`}
                          checked={isUserSelected(user.id)}
                          value={String(isUserSelected(user.id))}
                          onChange={() => handleUserSelect(user)}
                          label={false}
                          aria-label={`${user.firstName} ${user.lastName}`}
                        />
                      </td>
                      <td className={styles.tableCellName}>
                        <div className={styles.userName}>
                          {user.firstName}
                          {' '}
                          {user.lastName}
                        </div>
                        <div className={styles.userEmail}>{user.email}</div>
                      </td>
                      <td className={styles.tableCellRole}>
                        <div className={styles.roleCell}>
                          <RoleIcon role={user.role} />
                          <p className={styles.role}>{`${user.role}`}</p>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
          {!isFetching && (!data?.data?.items || data.data.items.length === 0) && (
            <div style={{ textAlign: 'center', padding: 24 }}>{t('userManagement.noUsersFound')}</div>
          )}
        </div>
      </div>
      <div className={styles.buttonWrapper}>
        <Button
          title={t('common.cancel')}
          variant={BUTTON_VARIANT.cancel}
          className={styles.button}
          onClick={onCancelUserSelect}
        />
        <Button
          title={t('common.add')}
          onClick={() => {
            setValueForm(TASK_ASSIGNED_TO, tempSelectedUsers, { shouldDirty: true });
            onClose();
          }}
          className={styles.button}
        />
      </div>
      <Modal
        open={isConfirmationModalOpen}
        title={t('taskManagement.creationProgressWillBeLost')}
        description={t('taskManagement.discardProgress')}
        confirmTitle={t('common.yesContinue')}
        closeTitle={t('common.cancel')}
        onReject={() => setIsConfirmationModalOpen(false)}
        onConfirm={onDiscardChangesConfirm}
        className={styles.confirmationModal}
      />
    </Modal>
  );
}

export default AddUserModal;
